/**
 * Maps permissions and routes to Apollo NavigationBar menuItems.
 * Replaces legacy AdminMenu, ReportMenu, and Permission-wrapped HeaderButton links.
 */
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { trialNetworksTabLinkURL } from 'spark-api/safetyLetter/commonSafetyLetterService';
import { NOT_FOUND_ROUTE } from 'spark-state/common/middleware/navigation/navigationMiddleware';
import { paths } from 'spark-views/safetyLetter/admin/adminRoutes';
import { paths as notificationPaths } from 'spark-views/safetyLetter/notifications/notificationRoutes';
import { paths as gapPacketPaths } from 'spark-views/gapPacketManagement/gapPacketRoutes';
import { paths as ibAdminPaths } from 'spark-views/InvestigatorBrochure/ibAdmin/ibAdminRoutes';
import { paths as ibRecipientPaths } from 'spark-views/InvestigatorBrochure/ibNotifications/ibNotificationsRoutes';
import { paths as enterprisePaths, navigation as enterpriseNavigation } from 'spark-views/enterprise/enterpriseRoutes';
import { paths as reportPaths } from 'spark-views/configuration/reportType/reportRoutes';
import { paths as bulkDownloadPaths } from 'spark-views/bulkDownload/downloadRoutes';
import { paths as auditReportPaths } from 'spark-views/safetyLetter/admin/report/auditReportRoutes';
import { paths as overdueAckPath } from 'spark-views/safetyLetter/admin/overdueAck/OverdueAckRoutes';
import { paths as manageRolesPath } from 'spark-views/safetyLetter/admin/manageRoles/manageRolesRoutes';
import { selectHeaderPermissions } from 'spark-util/Permission';

const REPORTING_PATHS = new Set([
    paths.details.safetyReport,
    gapPacketPaths.details.gapPacketReport,
    ibAdminPaths.details.ibReport,
]);

/** Admin submenu items formerly rendered by AdminMenu.js. */
const buildAdminSubmenu = (permissions, enterpriseId) => {
    const submenu = [];

    if (permissions.manageEnterprises) {
        submenu.push({
            text: 'Enterprise Management',
            pathname: enterprisePaths.enterpriseList,
        });
    }
    if (permissions.safetyAdmin) {
        submenu.push({
            text: 'Manage Users',
            pathname: enterpriseNavigation.enterpriseUsers(enterpriseId),
        });
    }
    if (permissions.manageRoles) {
        submenu.push({
            text: 'Manage Roles',
            pathname: manageRolesPath.root,
        });
    }
    if (permissions.reportTypeConfigurationModule) {
        submenu.push({
            text: 'Report Type Configuration',
            pathname: reportPaths.reportTypeList,
        });
    }
    if (permissions.downloadDocuments) {
        submenu.push({
            text: 'Bulk Download',
            pathname: bulkDownloadPaths.root,
        });
    }
    if (permissions.auditReports) {
        submenu.push({
            text: 'Audit Reports',
            pathname: auditReportPaths.root,
        });
    }
    if (permissions.overdueAckManagement) {
        submenu.push({
            text: 'Acknowledgement Mgmt',
            pathname: overdueAckPath.root,
        });
    }

    return submenu;
};

export function useSparkHeaderMenu({ enterpriseId, reportEnabled, resetReportType, history }) {
    const location = useLocation();
    const permissions = useSelector(selectHeaderPermissions);
    const isUserGuidePage = location.pathname.includes('/userGuides');
    const notFoundPath = NOT_FOUND_ROUTE;

    const menuItems = useMemo(() => {
        // SAF-18432: hide nav links on User Guide page (opened in new tab; profile menu remains).
        if (isUserGuidePage) {
            return [];
        }

        const items = [];

        if (permissions.safetyManagementModule) {
            items.push({
                text: 'Safety Home',
                pathname: enterpriseId ? paths.notifications : notFoundPath,
            });
        }
        if (permissions.manageGapPacket) {
            items.push({
                text: 'Gap Packet',
                pathname: enterpriseId ? gapPacketPaths.gapPacketList : notFoundPath,
            });
        }
        if (permissions.manageIB) {
            items.push({
                text: 'IB',
                pathname: enterpriseId ? ibAdminPaths.notifications : notFoundPath,
            });
        }
        if (permissions.safetyManagementModule && enterpriseId) {
            items.push({
                text: 'Safety Reporting',
                pathname: paths.details.safetyReport,
            });
        }
        if (permissions.manageGapPacket && enterpriseId) {
            items.push({
                text: 'Gap Packet Reporting',
                pathname: gapPacketPaths.details.gapPacketReport,
            });
        }
        if (permissions.manageIB && enterpriseId) {
            items.push({
                text: 'IB Reporting',
                pathname: ibAdminPaths.details.ibReport,
            });
        }
        if (permissions.safetyRecipient) {
            items.push({
                text: 'Safety Home',
                pathname: enterpriseId ? notificationPaths.notifications : notFoundPath,
            });
            if (reportEnabled) {
                items.push({
                    text: 'Safety Reporting',
                    pathname: notificationPaths.safetyReport,
                });
            }
        }
        if (permissions.gpRecipient) {
            items.push({
                text: 'Gap Packet',
                pathname: enterpriseId ? gapPacketPaths.acknowledge : notFoundPath,
            });
        }
        if (permissions.ibRecipient) {
            items.push({
                text: 'IB',
                pathname: enterpriseId ? ibRecipientPaths.notifications : notFoundPath,
            });
        }

        items.push({
            text: 'Site Portal',
            externalUrl: trialNetworksTabLinkURL,
        });

        if (permissions.enterpriseModule) {
            const adminSubmenu = buildAdminSubmenu(permissions, enterpriseId);
            if (adminSubmenu.length > 0) {
                items.push({
                    text: 'Admin',
                    menuItems: adminSubmenu,
                });
            }
        }

        return items;
    }, [
        isUserGuidePage,
        permissions,
        enterpriseId,
        reportEnabled,
        notFoundPath,
    ]);

    const onNavClick = useCallback(
        (item) => {
            if (item.externalUrl) {
                window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                return;
            }

            if (item.pathname) {
                // Clear stale report type when entering a reporting dashboard.
                if (REPORTING_PATHS.has(item.pathname)) {
                    resetReportType();
                }
                history.push(item.pathname);
            }
        },
        [history, resetReportType]
    );

    const checkIsActive = useCallback(
        (item) => {
            if (item.pathname) {
                return item.pathname === location.pathname;
            }
            if (item.menuItems) {
                return item.menuItems.some(
                    (subItem) => subItem.pathname && subItem.pathname === location.pathname
                );
            }
            return false;
        },
        [location.pathname]
    );

    return { menuItems, onNavClick, checkIsActive };
}
